import React, { FC, createRef, useEffect, useRef, useState } from 'react';
import { CatSideMenu } from 'components/blocks-components';
import { getPartsDetails } from 'services/parts-diagram/actions';
import cx from 'classnames';
import { MenuList } from './menu-list';
import styles from './parts-menu.module.scss';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import {
  CATEGORY_TYPES,
  ComponentsType,
  GroupPartsType,
  MenuIndex,
  SystemDataResponseType
} from 'types/parts-diagram';

interface PartsMenuProps {
  systemsData: SystemDataResponseType[];
  handleSearchParams: (menuIndexes: MenuIndex[]) => void;
}

const PartsMenu: FC<PartsMenuProps> = ({ systemsData, handleSearchParams }) => {
  const [menuIndex, setMenuIndex] = useState<MenuIndex[] | null>(null);
  const scrollRefs = useRef({});
  const { handlePartsContentCategory, handleSelectedPart } = usePartDiagram();

  useEffect(() => {
    let prevI = -1,
      prevJ = -1;
    const getGroupParts = (
      sys: SystemDataResponseType,
      comp: ComponentsType,
      i: number,
      j: number
    ) =>
      (comp?.groupParts ?? []).flatMap((grp, k) => {
        const partId = `${grp?.partNumber}${grp?.ieSystemControlNumber ?? ''}`;
        scrollRefs.current[partId] = scrollRefs.current[partId] ?? createRef();
        const sysItem =
          i !== prevI
            ? {
                indexArr: [i, -1, -1],
                data: {
                  id: `${sys?.id}${sys?.id}`,
                  systemId: sys?.id,
                  componentId: '',
                  partNumber: '',
                  ieSystemControlNumber: ''
                }
              }
            : [];
        const compItem =
          j !== prevJ
            ? {
                indexArr: [i, j, -1],
                data: {
                  id: `${sys?.id}${comp?.id}`,
                  systemId: sys?.id,
                  componentId: comp?.id,
                  partNumber: '',
                  ieSystemControlNumber: ''
                }
              }
            : [];
        prevI = i;
        prevJ = j;
        return [
          sysItem,
          compItem,
          {
            indexArr: [i, j, k],
            data: {
              id: null,
              systemId: sys?.id,
              componentId: comp?.id,
              partNumber: grp?.partNumber,
              ieSystemControlNumber: grp?.ieSystemControlNumber
            }
          }
        ].flat(1);
      });
    const data = (systemsData ?? []).flatMap((sys, i) => {
      prevJ = -1;
      return (sys?.components ?? []).flatMap((comp, j) =>
        getGroupParts(sys, comp, i, j)
      );
    });
    setMenuIndex(data);
  }, [systemsData]);

  useEffect(() => {
    if (!menuIndex) {
      return;
    }
    handleSearchParams(menuIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuIndex]);

  const handleComponentClick = (components: ComponentsType) => {
    handlePartsContentCategory(CATEGORY_TYPES.PARTSCARDS, components);
  };
  const handleGroupItemClick = async (groupItem: GroupPartsType) => {
    handlePartsContentCategory(CATEGORY_TYPES.LOADER);
    const { ieSystemControlNumber = '' } = groupItem || {};
    const { partDetailsAndSchematicResponse } = await getPartsDetails(
      ieSystemControlNumber
    );
    if (partDetailsAndSchematicResponse) {
      handlePartsContentCategory(
        CATEGORY_TYPES.PARTSDETAILS,
        null,
        partDetailsAndSchematicResponse
      );
    }
    handleSelectedPart(null);
  };

  return (
    <div
      className={cx(
        'w-100 d-flex flex-column overflow-x-hidden overflow-y-auto',
        styles['cat-side-menu__container']
      )}
    >
      <CatSideMenu className={styles['parts-diagram-menu']}>
        <MenuList
          systemsData={systemsData}
          menuIndex={menuIndex}
          scrollRefs={scrollRefs}
          handleComponentClick={handleComponentClick}
          handleGroupItemClick={handleGroupItemClick}
        />
      </CatSideMenu>
    </div>
  );
};

export default PartsMenu;
